import React, { Dispatch, SetStateAction } from 'react';
import styled from 'styled-components';

import { JourneyParticipant } from '@/features/journey/admin/atoms/JourneyParticipant';
import { Button } from '@/ui/Button';
import { FilterChip } from '@/ui/Chip';
import { Skeleton } from '@/ui/Skeleton';
import { ManagementEmptyStateAfterProcessed } from '@/features/management/manager/atoms/ManagementEmptyStateAfterProcessed';
import { Input } from '@/ui/TextField';
import { UserWithRole } from '@/administration/types/users';

const RootStyled = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const LearnersListStyled = styled.div`
  max-height: 600px;
  overflow-y: scroll;
  padding-right: 16px;
`;

const LoadMoreWrap = styled.div`
  display: flex;
  justify-content: center;
`;

const LearnerWrapStyled = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SelectedCounterContainerStyled = styled.div`
  display: flex;
  justify-content: flex-end;
  ${({ theme }) => theme.typography.body3};
`;

type ParticipantsPickerProps = {
  setSelectedUserIds: Dispatch<SetStateAction<number[]>>;
  selectedUserIds: number[];
  alreadyAddedUserIds: number[];
  loading: boolean;
  usersList: UserWithRole[];
  onLoadMore: () => void;
  canShowMore: boolean;
  onSearchPhraseChange: (value: string) => void;
  searchPhrase: string;
};

const LoadingState = () => (
  <LearnersListStyled>
    {[...Array(10)].map((_, i) => (
      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <JourneyParticipant.LoadingState />
        <Skeleton width={60} height={32} />
      </div>
    ))}
  </LearnersListStyled>
);

const EmptyStateNoSearchResults = ({ onClear }: { onClear: () => void }) => (
  <ManagementEmptyStateAfterProcessed onFiltersClear={onClear} />
);

export const ParticipantsPicker = ({
  selectedUserIds,
  setSelectedUserIds,
  alreadyAddedUserIds,
  loading,
  usersList,
  onLoadMore,
  canShowMore,
  onSearchPhraseChange,
  searchPhrase,
}: ParticipantsPickerProps): JSX.Element => {
  const stagedCount = selectedUserIds.filter((id) => !alreadyAddedUserIds.includes(id)).length;

  const renderListWithStates = () => {
    if (loading && !usersList.length) return <LoadingState />;

    if (!usersList?.length) return <EmptyStateNoSearchResults onClear={() => onSearchPhraseChange('')} />;

    return (
      <div>
        <LearnersListStyled>
          {usersList.map((user) => {
            const alreadyAdded = alreadyAddedUserIds.includes(user.id);
            const selected = selectedUserIds.includes(user.id);

            return (
              <React.Fragment key={user.id}>
                <LearnerWrapStyled>
                  <JourneyParticipant email={user.email} name={user.username || ''} profileImage={user.profileImage} />
                  {alreadyAdded ? (
                    // `selected` is what gives the disabled chip its checkmark and grey fill.
                    <FilterChip label="Already added" selected disabled />
                  ) : (
                    <FilterChip
                      label={selected ? 'Selected' : 'Select'}
                      selected={selected}
                      onClick={() =>
                        setSelectedUserIds((prev) => (selected ? prev.filter((id) => id !== user.id) : [...prev, user.id]))
                      }
                    />
                  )}
                </LearnerWrapStyled>
              </React.Fragment>
            );
          })}
          {canShowMore && (
            <LoadMoreWrap>
              <Button variant="secondary" onClick={onLoadMore} disabled={false}>
                Show more
              </Button>
            </LoadMoreWrap>
          )}
        </LearnersListStyled>
      </div>
    );
  };

  return (
    <RootStyled>
      {/* Empty label keeps the field placeholder-only, per the design. */}
      <Input
        label=""
        placeholder="Search name or email"
        leadingIcon="search"
        value={searchPhrase}
        onChange={(e) => onSearchPhraseChange(e.currentTarget.value)}
      />
      <div>
        {Boolean(stagedCount) && (
          <SelectedCounterContainerStyled>
            {stagedCount} learner{stagedCount > 1 ? 's' : ''} selected
          </SelectedCounterContainerStyled>
        )}

        {renderListWithStates()}
      </div>
    </RootStyled>
  );
};
